// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — SubprocessNode: 90×38, double vertical lines
// M2: Fuente uniforme T.font.nodeTitle, sin descripción
// M4: Edición inline con doble clic (data.editing)
// M8: NodeResizer controlado por data.resizeEnabled
// ═══════════════════════════════════════════════════════════════

import { memo, useCallback, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, useReactFlow } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';
import type { SubprocessNodeData, FlowNodeData } from '../../types/flowchart.types';

const W = T.node.width;
const H = T.node.height;
const LINE_OFFSET = 7;
const EXPANDED_W = 220;
const EXPANDED_H = 140;

function SubprocessNodeComponent({ id, data, selected }: NodeProps) {
  const d = data as unknown as SubprocessNodeData;
  const stroke = selected ? T.color.nodeStrokeSelected : T.color.nodeStroke;
  const strokeW = selected ? T.shape.strokeWidthSelected : T.shape.strokeWidth;
  const expanded = d.expanded === true;
  const { setNodes, updateNodeData } = useReactFlow();

  const [editValue, setEditValue] = useState(d.label);
  const inputRef = useRef<HTMLInputElement>(null);
  const editing = !!(d as any).editing;

  useEffect(() => {
    if (editing) {
      setEditValue(d.label);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editing, d.label]);

  const saveEdit = useCallback(() => {
    updateNodeData(id, { label: editValue, editing: false } as Partial<FlowNodeData>);
  }, [id, editValue, updateNodeData]);

  const cancelEdit = useCallback(() => {
    updateNodeData(id, { editing: false } as Partial<FlowNodeData>);
  }, [id, updateNodeData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation(); // Prevent React Flow from capturing keys (Backspace, Delete, etc.)
      if (e.key === 'Enter') saveEdit();
      if (e.key === 'Escape') cancelEdit();
    },
    [saveEdit, cancelEdit],
  );

  const toggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  expanded: !(n.data as FlowNodeData as SubprocessNodeData).expanded,
                } as FlowNodeData,
              }
            : n,
        ),
      );
    },
    [id, setNodes],
  );

  const currentW = expanded ? EXPANDED_W : W;
  const currentH = expanded ? EXPANDED_H : H;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        minWidth: currentW,
        minHeight: currentH,
      }}
    >
      <NodeResizer
        minWidth={60}
        minHeight={30}
        isVisible={!!selected && !!(d as any).resizeEnabled}
        lineClassName="node-resizer-line"
        handleClassName="node-resizer-handle"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-t"
        style={{ background: T.color.nodeStroke, width: 4, height: 4, border: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-s"
        style={{ background: 'transparent', width: 10, height: 10, border: 'none' }}
      />

      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${currentW} ${currentH}`}
        preserveAspectRatio="none"
      >
        <rect
          x={T.shape.strokeWidth / 2}
          y={T.shape.strokeWidth / 2}
          width={currentW - T.shape.strokeWidth}
          height={currentH - T.shape.strokeWidth}
          rx={T.shape.borderRadius}
          ry={T.shape.borderRadius}
          fill={T.color.nodeFill}
          stroke={stroke}
          strokeWidth={strokeW}
          strokeDasharray={expanded ? '4 2' : 'none'}
        />
        <line
          x1={LINE_OFFSET}
          y1={1}
          x2={LINE_OFFSET}
          y2={currentH - 1}
          stroke={T.color.nodeStroke}
          strokeWidth={0.5}
        />
        <line
          x1={currentW - LINE_OFFSET}
          y1={1}
          x2={currentW - LINE_OFFSET}
          y2={currentH - 1}
          stroke={T.color.nodeStroke}
          strokeWidth={0.5}
        />
      </svg>

      {/* Zona de etiqueta */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: expanded ? 28 : currentH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: T.font.family,
          pointerEvents: editing ? 'auto' : 'none',
          padding: `0 ${LINE_OFFSET + 2}px`,
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={saveEdit}
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              fontFamily: T.font.family,
              color: T.color.textPrimary,
              textAlign: 'center',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              width: '100%',
              padding: 0,
            }}
          />
        ) : (
          <span
            style={{
              fontSize: T.font.nodeTitle.size,
              fontWeight: T.font.nodeTitle.weight,
              color: T.color.textPrimary,
              textAlign: 'center',
              lineHeight: 1.2,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
            }}
            title={d.label}
          >
            {d.label}
          </span>
        )}
      </div>

      {/* Contenido expandido (placeholder) */}
      {expanded && (
        <div
          style={{
            position: 'absolute',
            top: 30,
            left: LINE_OFFSET + 4,
            right: LINE_OFFSET + 4,
            bottom: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px dashed ${T.color.border}`,
            borderRadius: 3,
            fontSize: '7px',
            fontFamily: T.font.family,
            color: T.color.textTertiary,
          }}
        >
          Subnodos del subproceso
        </div>
      )}

      {/* Boton expandir/colapsar [+]/[-] */}
      <button
        onClick={toggleExpand}
        style={{
          position: 'absolute',
          top: 2,
          right: LINE_OFFSET + 2,
          width: 12,
          height: 12,
          borderRadius: 2,
          border: `1px solid ${T.color.nodeStroke}`,
          backgroundColor: T.color.surface,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '8px',
          fontWeight: 700,
          fontFamily: 'monospace',
          color: T.color.textSecondary,
          lineHeight: 1,
          padding: 0,
          pointerEvents: 'all',
        }}
        title={expanded ? 'Colapsar subproceso' : 'Expandir subproceso'}
      >
        {expanded ? '\u2212' : '+'}
      </button>
    </div>
  );
}

export const SubprocessNode = memo(SubprocessNodeComponent);
